import { NavigationMenu } from '@base-ui/react/navigation-menu';
import { colors, darkThemeSelector, shadows, styled, zIndexes } from '@meterup/atto';
import { type CSSProperties, type ReactNode, createContext, useContext } from 'react';

import { sidebarVars } from './vars';

const SidebarTreeFlyoutScopeContext = createContext(false);

export function useSidebarTreeFlyoutShared() {
  return useContext(SidebarTreeFlyoutScopeContext);
}

export const sidebarTreeFlyoutListStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const SidebarTreeFlyoutList = styled(NavigationMenu.List, {
  listStyle: 'none',
  padding: `0 $${sidebarVars.padding.sides}`,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});
SidebarTreeFlyoutList.displayName = 'SidebarTreeFlyoutList';

const SidebarTreeFlyoutPopup = styled(NavigationMenu.Popup, {
  position: 'relative',
  zIndex: zIndexes.flyout,
  borderRadius: '$8',
  backgroundColor: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,
  outline: 'none',
  transformOrigin: 'var(--transform-origin, left center)',
  transitionProperty: 'opacity, transform, width, height',
  transitionDuration: '320ms',
  transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',

  '&[data-starting-style]': {
    opacity: 0.92,
    transform: 'scale(0.98)',
  },

  '&[data-ending-style]': {
    opacity: 0.92,
    transform: 'scale(0.98)',
  },

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },
});
SidebarTreeFlyoutPopup.displayName = 'SidebarTreeFlyoutPopup';

const SidebarTreeFlyoutViewport = styled(NavigationMenu.Viewport, {
  position: 'relative',
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  transitionProperty: 'width, height',
  transitionDuration: '320ms',
  transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
});
SidebarTreeFlyoutViewport.displayName = 'SidebarTreeFlyoutViewport';

export {
  SidebarTreeFlyoutPopup as SidebarTreeFlyoutPopupFrame,
  SidebarTreeFlyoutViewport as SidebarTreeFlyoutViewportFrame,
};

/**
 * One NavigationMenu root + viewport for every minimized sidebar tree inside it, so the
 * flyout panel slides between items (Base UI viewport pattern).
 */
export function SidebarTreeFlyout({ children }: { children: ReactNode }) {
  return (
    <SidebarTreeFlyoutScopeContext.Provider value>
      <NavigationMenu.Root orientation="vertical" delay={80} closeDelay={150}>
        <SidebarTreeFlyoutList>{children}</SidebarTreeFlyoutList>
        <NavigationMenu.Portal>
          <NavigationMenu.Positioner side="right" sideOffset={4} align="start" collisionPadding={8}>
            <SidebarTreeFlyoutPopup>
              <SidebarTreeFlyoutViewport />
            </SidebarTreeFlyoutPopup>
          </NavigationMenu.Positioner>
        </NavigationMenu.Portal>
      </NavigationMenu.Root>
    </SidebarTreeFlyoutScopeContext.Provider>
  );
}
